'use client';

import React, { useEffect, useState } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { Layers } from 'lucide-react';
import { useMountShuffledList } from './shuffle-utils';

interface QuickClassificationMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; matches?: Record<string, string>; hints?: any[] };
  payload: {
    categories: Array<{ id: string; label: string }>;
    items: Array<{ id: string; content: string }>;
    shuffle?: boolean;
    _hasHints?: boolean;
  };
}

export default function QuickClassificationMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, payload }: any) {
  const actualMechanicId = mechanicId || id || '';
  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={prompt || ''}
      icon={<Layers className="w-5 h-5" />}
      iconLabel="Quick Classification — Sort items into categories"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
    >
      {(submissionState, setAnswer) => (
        <QuickClassificationMechanicInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} />
      )}
    </BaseMechanicShell>
  );
}


function QuickClassificationMechanicInner({ payload, submissionState, setAnswer }: any) {
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  const shouldShuffle = payload?.shuffle !== false; // shuffle by default

  const shuffledItems = useMountShuffledList(payload?.items || [], shouldShuffle);
  const shuffledCategories = useMountShuffledList(payload?.categories || [], shouldShuffle);

  const items = shuffledItems;
  const categories = shuffledCategories;

  const unassignedItems = items.filter((item: any) => !selections[item.id]);

  const toggleAssignment = (itemId: string, categoryId: string) => {
    if (submissionState !== 'idle' && submissionState !== 'error') return;
    setSelections(prev => {
      const newSel = { ...prev };
      if (newSel[itemId] === categoryId) {
        delete newSel[itemId]; // Unassign
      } else {
        newSel[itemId] = categoryId;
      }
      return newSel;
    });
  };

  const handleBucketClick = (categoryId: string) => {
    if (activeItemId) {
      toggleAssignment(activeItemId, categoryId);
      setActiveItemId(null);
    }
  };

  useEffect(() => {
    if (Object.keys(selections).length === items.length && items.length > 0) {
      setAnswer(selections);
    } else {
      setAnswer(null);
    }
  }, [selections, setAnswer, items.length]);

  const leftCats = categories.slice(0, Math.ceil(categories.length / 2));
  const rightCats = categories.slice(Math.ceil(categories.length / 2));

  const renderBucket = (cat: any) => {
    const assigned = items.filter((item: any) => selections[item.id] === cat.id);
    const isActiveTarget = activeItemId !== null;
    
    return (
      <div 
        key={cat.id} 
        onClick={() => handleBucketClick(cat.id)}
        className={`flex flex-col gap-3 p-5 border-2 rounded-2xl h-full min-h-[160px] shadow-sm transition-all duration-300 ${
          isActiveTarget 
            ? 'bg-primary/5 border-primary/50 cursor-pointer hover:bg-primary/10 hover:shadow-md' 
            : 'bg-muted/20 border-border/60'
        }`}
      >
        <h4 className="font-bold text-center text-sm text-foreground/80 uppercase tracking-widest">{cat.label}</h4>
        <div className="flex flex-col gap-2 flex-1">
          {assigned.map((item: any) => (
            <div 
              key={item.id} 
              onClick={(e) => {
                e.stopPropagation(); // Prevent bucket click
                toggleAssignment(item.id, cat.id);
              }}
              className="p-3 bg-primary text-primary-foreground text-sm font-medium rounded-xl shadow-sm cursor-pointer hover:bg-destructive hover:text-destructive-foreground hover:line-through transition-all duration-200"
            >
              {item.content}
            </div>
          ))}
          {assigned.length === 0 && (
            <div className={`flex-1 flex items-center justify-center border-2 border-dashed rounded-xl min-h-[80px] transition-colors ${
              isActiveTarget ? 'border-primary/50 bg-primary/5' : 'border-border/40'
            }`}>
               <span className={`text-xs font-semibold uppercase tracking-wider ${isActiveTarget ? 'text-primary animate-pulse' : 'text-muted-foreground opacity-50'}`}>
                 {isActiveTarget ? 'Click to slot here' : 'Drop Here'}
               </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-8 w-full mt-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-start">
        
        {/* Left Buckets */}
        <div className="flex flex-col gap-6 w-full">
          {leftCats.map(renderBucket)}
        </div>

        {/* Center: Unassigned Pool */}
        <div className="flex flex-col gap-4 items-center w-full">
          <div className="inline-flex items-center justify-center px-4 py-1.5 bg-muted rounded-full">
            <span className="font-bold text-xs text-muted-foreground uppercase tracking-widest">
              Unassigned ({unassignedItems.length})
            </span>
          </div>
          
          <div className="flex flex-col gap-3 w-full max-w-[320px]">
            {unassignedItems.map((item: any) => {
              const isActive = activeItemId === item.id;
              return (
                <div 
                  key={item.id} 
                  onClick={() => setActiveItemId(isActive ? null : item.id)}
                  className={`p-4 border-2 shadow-md rounded-xl text-sm font-semibold text-center cursor-pointer transition-all duration-300 select-none ${
                    isActive 
                      ? 'bg-primary border-primary text-primary-foreground scale-105 shadow-lg' 
                      : 'bg-background border-border/80 hover:border-primary/50 text-foreground hover:-translate-y-0.5'
                  }`}
                >
                  {item.content}
                  {isActive && (
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-primary-foreground/80 animate-pulse">
                      Select a bucket
                    </div>
                  )}
                </div>
              );
            })}
            {unassignedItems.length === 0 && (
              <div className="p-8 border-2 border-dashed border-primary/30 bg-primary/5 rounded-2xl text-center flex flex-col items-center justify-center gap-3">
                 <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                   <Layers className="w-5 h-5 text-primary" />
                 </div>
                 <span className="text-sm font-bold text-primary">All items sorted!</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Buckets */}
        <div className="flex flex-col gap-6 w-full">
          {rightCats.map(renderBucket)}
        </div>

      </div>
    </div>
  );
}
